import { unzip } from 'fflate';
import AppConfig from '../AppConfig';
import Constants from '../Constants';
import { getAuthtoken } from './helper';

const DROPBOX_SCRIPT_ID = 'dropboxjs';

export const loadDropboxChooser = (appKey) => new Promise((resolve, reject) => {
  if (!appKey) {
    reject(new Error('Missing Dropbox app key'));
    return;
  }

  if (window.Dropbox) {
    resolve(window.Dropbox);
    return;
  }

  const existingScript = document.getElementById(DROPBOX_SCRIPT_ID);
  if (existingScript) {
    existingScript.addEventListener('load', () => resolve(window.Dropbox));
    existingScript.addEventListener('error', () => reject(new Error('Failed to load Dropbox Chooser')));
    return;
  }

  const script = document.createElement('script');
  script.id = DROPBOX_SCRIPT_ID;
  script.type = 'text/javascript';
  script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
  script.dataset.appKey = appKey;
  script.onload = () => resolve(window.Dropbox);
  script.onerror = () => reject(new Error('Failed to load Dropbox Chooser'));
  document.body.appendChild(script);
});

export const buildDropboxExtensions = (accept) => {
  if (!accept) return [];
  return accept
    .split(',')
    .map((entry) => entry.trim())
    .map((entry) => {
      if (entry.startsWith('.')) return entry;
      if (entry === 'application/pdf') return '.pdf';
      return '';
    })
    .filter(Boolean);
};

const toDirectDropboxLink = (link) => {
  try {
    const url = new URL(link);
    if (url.hostname.endsWith('dropbox.com')) {
      url.searchParams.set('dl', '1');
      return url.toString();
    }
    return link;
  } catch {
    return link;
  }
};

const fetchDropboxBlob = async (link, fileName) => {
  const response = await fetch(link);
  if (!response.ok) {
    throw new Error(`Failed to fetch Dropbox file: ${fileName}`);
  }
  const contentType = response.headers.get('content-type') || '';
  const blob = await response.blob();
  return { blob, contentType };
};

const inferFileTypeFromName = (fileName = '') => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.heif')) return 'image/heif';
  if (lower.endsWith('.doc')) return 'application/msword';
  if (lower.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  return '';
};

const downloadDropboxLinkViaProxy = async ({ link, fileName }) => {
  const endpoint = `${AppConfig.PfqBaseUrl.replace(/\/$/, '')}/${Constants.ApiUrl.partner.dropboxDownload}`;
  const token = getAuthtoken();

  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ link })
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');

    throw new Error(`Failed to download "${fileName}" (HTTP ${response.status}): ${detail.slice(0, 300)}`);
  }

  return response.blob();
};

// A selected folder arrives as one link, which the proxy resolves to a zip we expand in the browser
const fetchDropboxFolderAsFiles = async ({ folder }) => {
  const blob = await downloadDropboxLinkViaProxy({ link: folder.link, fileName: folder.name });
  const bytes = new Uint8Array(await blob.arrayBuffer());

  const unzipped = await new Promise((resolve, reject) => {
    unzip(bytes, (error, data) => (error ? reject(error) : resolve(data)));
  });

  return Object.entries(unzipped)
    .filter(([path, content]) => !path.endsWith('/') && content.length > 0)
    .map(([path, content]) => {
      const name = path.split('/').pop();

      return new File([content], name, { type: inferFileTypeFromName(name) });
    });
};

const fetchDropboxFileViaProxy = async ({ item }) => {
  const blob = await downloadDropboxLinkViaProxy({ link: item.link, fileName: item.name });

  return new File([blob], item.name, { type: inferFileTypeFromName(item.name) || blob.type });
};

// One failed item must not lose the rest of the selection, so failures are logged and dropped
export const fetchDropboxSelectionAsFiles = async (selection) => {
  const results = await Promise.allSettled(
    (selection || []).map(async (item) =>
      (item.isDir ? fetchDropboxFolderAsFiles({ folder: item }) : [await fetchDropboxFileViaProxy({ item })])
    )
  );

  const failures = results.filter((result) => result.status === 'rejected');

  if (failures.length) {
    console.error('[Dropbox] some items failed to download:', failures.map((failure) => failure.reason?.message));
  }

  return results
    .filter((result) => result.status === 'fulfilled')
    .flatMap((result) => result.value);
};

export const fetchDropboxFilesAsLocal = async (dropboxFiles) => {
  const downloadedFiles = await Promise.all(
    (dropboxFiles || []).map(async (dropboxFile) => {
      let { blob, contentType } = await fetchDropboxBlob(dropboxFile.link, dropboxFile.name);

      if (contentType.includes('text/html')) {
        const directLink = toDirectDropboxLink(dropboxFile.link);
        if (directLink !== dropboxFile.link) {
          const retry = await fetchDropboxBlob(directLink, dropboxFile.name);
          blob = retry.blob;
          contentType = retry.contentType;
        }
      }

      const inferredType = inferFileTypeFromName(dropboxFile.name);
      const fileType = contentType.includes('text/html')
        ? inferredType
        : blob.type || contentType || inferredType;

      return new File([blob], dropboxFile.name, {
        type: fileType
      });
    })
  );

  return downloadedFiles;
};
